/**
 * Modal — Centered overlay dialog for focused tasks, confirmations, or forms.
 *
 * Modal Component Guidelines
 * - Use Modal when the user needs to complete a focused task or make a decision
 *   before returning to the main UI. For lightweight, contextual content anchored
 *   to a trigger, use Popover instead. For side panels, use Sheet or Drawer.
 * - Use `size="sm"` for simple confirmations and alerts, `size="base"` for forms
 *   and standard dialogs, `size="lg"` for data-heavy content like tables or previews.
 * - Set `dismissable={false}` for flows that require an explicit choice (e.g.
 *   multi-step wizards, payment confirmation) — prevents accidental closure.
 * - Hide the close button (`showCloseButton={false}`) only when the footer already
 *   provides explicit cancel/confirm actions and you want to force a deliberate choice.
 * - Prefer the simple `<Modal>` for standard title/description/footer layouts.
 *   Switch to the composable API (`ModalRoot` / `ModalContent` / etc.) only when
 *   you need non-standard content structure (e.g. scrollable body with sticky footer,
 *   multi-column layout).
 * @see https://base-ui.com/react/components/dialog Base UI Dialog documentation
 * @see https://ui.shadcn.com/docs/components/dialog shadcn/ui Dialog documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import React from "react";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { Button } from "@replohq/lattice/components/button/Button";
import {
  PortalThemeScope,
  usePortalContainer,
} from "@replohq/lattice/components/portal-theme/PortalTheme";
import { cn } from "@replohq/lattice/utils/cn";
import { cva } from "class-variance-authority";
import { XIcon } from "lucide-react";

type ModalSize = "sm" | "base" | "lg";

// The header sizes its type from these and keeps its text clear of the close button, which the content positions.
const ModalLayoutContext = React.createContext<{
  size: ModalSize;
  showCloseButton: boolean;
}>({ size: "base", showCloseButton: true });

const modalContentVariants = cva(
  "bg-sheet text-default shadow-lg data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 ring-transparent border border-border border-0.5 grid max-w-[calc(100%-2rem)] max-h-[calc(100%-2rem)] overflow-hidden rounded-[20px] text-sm ring-1 duration-100 fixed top-1/2 left-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 outline-none gap-4",
  {
    variants: {
      size: {
        sm: "p-6 sm:max-w-sm",
        base: "p-6 sm:max-w-2xl",
        lg: "p-8 sm:max-w-6xl",
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

const modalHeaderVariants = cva("flex flex-col", {
  variants: {
    size: {
      sm: "gap-1",
      base: "gap-1",
      lg: "gap-2",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

const modalTitleVariants = cva(
  "text-default font-sans font-medium leading-tight",
  {
    variants: {
      size: {
        sm: "text-base",
        base: "text-base",
        lg: "text-xl",
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

const modalDescriptionVariants = cva(
  "text-muted *:[a]:hover:text-default *:[a]:underline *:[a]:underline-offset-3",
  {
    variants: {
      size: {
        sm: "text-sm",
        base: "text-sm",
        lg: "text-base",
      },
    },
    defaultVariants: {
      size: "base",
    },
  },
);

function ModalRoot({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="modal" {...props} />;
}

interface ModalTriggerProps<ClassName extends string = string> extends Omit<
  DialogPrimitive.Trigger.Props,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ModalTrigger<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ModalTriggerProps<ClassName>) {
  return (
    <DialogPrimitive.Trigger
      data-slot="modal-trigger"
      className={cn(className, UNSAFE_className)}
      {...props}
    />
  );
}

function ModalPortal({ children, ...props }: DialogPrimitive.Portal.Props) {
  const container = usePortalContainer();
  return (
    <DialogPrimitive.Portal
      data-slot="modal-portal"
      container={container}
      {...props}
    >
      <PortalThemeScope>{children}</PortalThemeScope>
    </DialogPrimitive.Portal>
  );
}

interface ModalPopupProps<ClassName extends string = string> extends Omit<
  DialogPrimitive.Popup.Props,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

const ModalPopupInner = <ClassName extends string>(
  { className, UNSAFE_className, ...props }: ModalPopupProps<ClassName>,
  ref: React.ForwardedRef<HTMLDivElement>,
) => {
  return (
    <DialogPrimitive.Popup
      ref={ref}
      data-slot="modal-popup"
      className={cn(className, UNSAFE_className)}
      {...props}
    />
  );
};

// forwardRef erases the generic ClassName param; the cast re-applies the
// generic signature so callers can pass literal layout classNames.
// eslint-disable-next-line replo/no-as-cast
const ModalPopup = React.forwardRef(ModalPopupInner) as <
  ClassName extends string,
>(
  props: ModalPopupProps<ClassName> & React.RefAttributes<HTMLDivElement>,
) => React.ReactNode;

function ModalClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="modal-close" {...props} />;
}

interface ModalOverlayProps<ClassName extends string = string> extends Omit<
  DialogPrimitive.Backdrop.Props,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ModalOverlay<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ModalOverlayProps<ClassName>) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="modal-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 bg-default/10 duration-100 supports-backdrop-filter:backdrop-blur-xs fixed inset-0 isolate z-50",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface ModalContentProps<ClassName extends string = string> extends Omit<
  DialogPrimitive.Popup.Props,
  "className"
> {
  showCloseButton?: boolean;
  overlayUNSAFE_className?: string;
  /**
   * The size of the modal, affecting width, padding, gap, and child typography.
   * @default "base"
   */
  size?: ModalSize;
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ModalContent<ClassName extends string>({
  className,
  UNSAFE_className,
  children,
  showCloseButton = true,
  overlayUNSAFE_className,
  size = "base",
  ...props
}: ModalContentProps<ClassName>) {
  return (
    <ModalLayoutContext.Provider value={{ size, showCloseButton }}>
      <ModalPortal>
        <ModalOverlay UNSAFE_className={overlayUNSAFE_className} />
        <DialogPrimitive.Popup
          data-slot="modal-content"
          className={cn(
            modalContentVariants({ size }),
            className,
            UNSAFE_className,
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="modal-close"
              render={
                <Button
                  variant="tertiary"
                  icon
                  size="base"
                  className="absolute top-5 right-5"
                  UNSAFE_className="p-2"
                />
              }
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Popup>
      </ModalPortal>
    </ModalLayoutContext.Provider>
  );
}

function ModalHeader({ className, ...props }: React.ComponentProps<"div">) {
  const { size, showCloseButton } = React.useContext(ModalLayoutContext);
  return (
    <div
      data-slot="modal-header"
      className={cn(
        modalHeaderVariants({ size }),
        showCloseButton && "pr-10",
        className,
      )}
      {...props}
    />
  );
}

interface ModalFooterProps<ClassName extends string = string> extends Omit<
  React.ComponentProps<"div">,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ModalFooter<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ModalFooterProps<ClassName>) {
  return (
    <div
      data-slot="modal-footer"
      className={cn(
        "flex flex-row gap-3 items-center justify-end",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface ModalTitleProps<ClassName extends string = string> extends Omit<
  DialogPrimitive.Title.Props,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  visuallyHidden?: boolean;
}

function ModalTitle<ClassName extends string>({
  className,
  UNSAFE_className,
  visuallyHidden,
  ...props
}: ModalTitleProps<ClassName>) {
  const { size } = React.useContext(ModalLayoutContext);
  return (
    <DialogPrimitive.Title
      data-slot="modal-title"
      className={cn(
        modalTitleVariants({ size }),
        visuallyHidden && "sr-only",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface ModalDescriptionProps<ClassName extends string = string> extends Omit<
  DialogPrimitive.Description.Props,
  "className"
> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function ModalDescription<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: ModalDescriptionProps<ClassName>) {
  const { size } = React.useContext(ModalLayoutContext);
  return (
    <DialogPrimitive.Description
      data-slot="modal-description"
      className={cn(
        modalDescriptionVariants({ size }),
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

export type ModalProps<ClassName extends string = string> =
  React.PropsWithChildren<{
    title?: string;
    /** Names the dialog for assistive tech when there is no visible `title`. */
    accessibleTitle?: React.ReactNode;
    description?: string;
    trigger?: React.ReactNode;
    footer?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (
      open: boolean,
      eventDetails: DialogPrimitive.Root.ChangeEventDetails,
    ) => void;
    /**
     * Whether the modal can be dismissed by clicking outside.
     * @default true
     */
    dismissable?: boolean;
    /**
     * Whether to show the close button in the top-right corner.
     * @default true
     */
    showCloseButton?: boolean;
    /**
     * The size of the modal.
     * @default "base"
     */
    size?: ModalSize;
    /**
     * Layout class name applied to the modal content.
     */
    className?: LayoutClassName<ClassName>;
    /**
     * Escape hatch for non-layout classes on the modal content.
     */
    UNSAFE_className?: string;
    overlayUNSAFE_className?: string;
  }>;

function Modal<ClassName extends string>({
  title,
  accessibleTitle,
  description,
  trigger,
  footer,
  children,
  open,
  onOpenChange,
  dismissable = true,
  showCloseButton = true,
  size = "base",
  className,
  UNSAFE_className,
  overlayUNSAFE_className,
}: ModalProps<ClassName>) {
  return (
    <ModalRoot
      open={open}
      onOpenChange={onOpenChange}
      disablePointerDismissal={!dismissable}
    >
      {trigger && <ModalTrigger>{trigger}</ModalTrigger>}
      <ModalContent
        showCloseButton={showCloseButton}
        size={size}
        className={className}
        UNSAFE_className={UNSAFE_className}
        overlayUNSAFE_className={overlayUNSAFE_className}
      >
        {!title && accessibleTitle && (
          <ModalTitle>
            <span className="sr-only">{accessibleTitle}</span>
          </ModalTitle>
        )}
        {(title || description) && (
          <ModalHeader>
            {title && <ModalTitle>{title}</ModalTitle>}
            {description && <ModalDescription>{description}</ModalDescription>}
          </ModalHeader>
        )}
        {children}
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContent>
    </ModalRoot>
  );
}

export {
  Modal,
  ModalRoot,
  ModalPortal,
  ModalPopup,
  ModalOverlay,
  ModalTrigger,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalTitle,
  ModalDescription,
};
